export type StoryServerState =
  "loading" | "empty" | "partial" | "ready" | "stale" | "locked" | "error";

export interface StoryQuerySignal<T> {
  data: T | undefined;
  error?: unknown;
  isFetching: boolean;
  isPending: boolean;
  isStale: boolean;
  required?: boolean;
}

export interface StoryQuerySnapshot<TData extends Record<string, unknown>> {
  state: StoryServerState;
  data: Readonly<Partial<TData>>;
  failed: readonly (keyof TData)[];
  missing: readonly (keyof TData)[];
}

export interface StorySnapshotOptions {
  locked?: boolean;
  emptyWhen?: (data: Readonly<Record<string, unknown>>) => boolean;
}

interface StorySnapshotStatus {
  hasData: boolean;
  hasFailure: boolean;
  hasMissing: boolean;
  fetching: boolean;
  stale: boolean;
  locked: boolean;
  empty: boolean;
}

function classifyStoryState(status: StorySnapshotStatus): StoryServerState {
  if (status.locked) return "locked";
  if (status.fetching && !status.hasData) return "loading";
  if (status.hasFailure && !status.hasData) return "error";
  if (status.hasMissing || status.hasFailure) return "partial";
  if (status.empty) return "empty";
  if (status.stale || status.fetching) return "stale";
  return "ready";
}

/**
 * Convert immutable domain query signals into one explicit route snapshot.
 * The function is deliberately React-free so descriptor builders stay pure.
 */
export function buildStoryQuerySnapshot<TData extends Record<string, unknown>>(
  signals: { readonly [K in keyof TData]: StoryQuerySignal<TData[K]> },
  options: StorySnapshotOptions = {},
): StoryQuerySnapshot<TData> {
  const keys = Object.keys(signals) as (keyof TData)[];
  const data: Partial<TData> = {};
  const failed: (keyof TData)[] = [];
  const missing: (keyof TData)[] = [];
  let fetching = false;
  let stale = false;

  for (const key of keys) {
    const signal = signals[key];
    if (signal.data !== undefined) data[key] = signal.data;
    if (signal.error !== undefined && signal.error !== null) failed.push(key);
    if (signal.data === undefined && signal.required !== false)
      missing.push(key);
    fetching ||= signal.isFetching || signal.isPending;
    stale ||= signal.isStale;
  }

  const frozenData = Object.freeze({ ...data }) as Readonly<Partial<TData>>;
  const hasData = Object.keys(frozenData).length > 0;
  const state = classifyStoryState({
    hasData,
    hasFailure: failed.length > 0,
    hasMissing: missing.length > 0,
    fetching,
    stale,
    locked: options.locked === true,
    empty:
      options.emptyWhen?.(
        frozenData as Readonly<Record<string, unknown>>,
      ) === true,
  });

  return Object.freeze({
    state,
    data: frozenData,
    failed: Object.freeze([...failed]),
    missing: Object.freeze([...missing]),
  });
}

export const SEALED_STORY_ARCHETYPES = Object.freeze([
  "report",
  "product",
  "dashboard",
  "form",
  "map",
  "analysis",
  "operational",
] as const);

export type SealedStoryArchetype = (typeof SEALED_STORY_ARCHETYPES)[number];

export interface StoryDescriptorBuilder<TSnapshot, TCallbacks, TDescriptor> {
  archetype: SealedStoryArchetype;
  build: (snapshot: Readonly<TSnapshot>, callbacks: TCallbacks) => TDescriptor;
}

/** Marks a pure snapshot → descriptor adapter at its public domain boundary. */
export function defineStoryDescriptorBuilder<
  TSnapshot,
  TCallbacks,
  TDescriptor,
>(
  builder: StoryDescriptorBuilder<TSnapshot, TCallbacks, TDescriptor>,
): StoryDescriptorBuilder<TSnapshot, TCallbacks, TDescriptor> {
  return Object.freeze(builder);
}
